import React, { useState, useEffect } from "react";
import {
  Button, Box, TextField, Typography, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { useTheme } from "@mui/material";
import { format } from "date-fns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import RibbonNavbar from "../../components/RibbonNavbar"; // Assuming it's shared

const ViewBalance = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [rows, setRows] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [isEditable, setIsEditable] = useState(false);

  const columns = [
    { field: "slNo", headerName: "Sl.No", flex: 1, minWidth: 90, editable: isEditable },
    { field: "date", headerName: "Date", flex: 1, minWidth: 150, editable: isEditable },
    { field: "location", headerName: "Location", flex: 1, minWidth: 150, editable: isEditable },
    { field: "uploadId", headerName: "Upload ID", flex: 1, minWidth: 150, editable: isEditable },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="error"
          onClick={() => handleDelete(params.row.id)}
        >
          Delete
        </Button>
      ),
    },
  ];
  const handleRowEditCommit = (params) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === params.id ? { ...row, [params.field]: params.value } : row
      )
    );
  };
  const sampleRows = [
    { id: 1, slNo: 1, date: "02-03-2025", location: "Room No: 1", uploadId: "UP1234" },
    { id: 2, slNo: 2, date: "02-03-2025", location: "Store Room: 1", uploadId: "UP1235" },
    { id: 3, slNo: 3, date: "02-03-2025", location: "Store Room: 1", uploadId: "UP1285" },
    { id: 4, slNo: 4, date: "02-03-2025", location: "Store Room: 2", uploadId: "UP1239" },
  ];

  useEffect(() => {
    setRows(sampleRows);
    setLoading(false);
  }, []);

  const handleDelete = (id) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleEdit = () => {
    setIsEditable((prev) => !prev);
  };

  const handleDeleteFromRibbon = () => {
    setOpenConfirmDialog(true);
  };

  const confirmDelete = () => {
    setRows((prev) => prev.filter((row) => !selectedRows.includes(row.id)));
    setSelectedRows([]);
    setOpenConfirmDialog(false);
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  const handleSearchClick = () => {
    const filtered = sampleRows.filter(
      (row) =>
        row.uploadId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        row.location.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setRows(filtered);
  };

  return (
    <div style={{ padding: "10px 20px" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Header title="Customer Balance Report" subtitle="" />

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box display="flex" alignItems="center" gap={1}>
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={handleDateChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{
                    width: 160,
                    "& .MuiInputBase-root": { height: 35 },
                    "& .MuiInputBase-input": { padding: "6px 8px", fontSize: "0.85rem" },
                  }}
                />
              )}
            />
            <Button
              variant="contained"
              onClick={handleSearchClick}
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.primary[400],
                fontSize: "14px",
                fontWeight: "bold",
                minWidth: "100px",
              }}
            >
              Search
            </Button>
          </Box>
        </LocalizationProvider>

        <RibbonNavbar
          showEdit
          showDelete
          onEdit={handleEdit}
          onDelete={handleDeleteFromRibbon}
          disabledEdit={selectedRows.length !== 1}
          disabledDelete={selectedRows.length === 0}
        />
      </Box>

      {selectedDate && (
        <Typography variant="h6" style={{ marginTop: "10px" }}>
          Selected Date: {format(selectedDate, "dd-MM-yyyy")}
        </Typography>
      )}

      <Box mt={2}>
        <Box
          height="78vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid grey",
              whiteSpace: "normal",
              wordWrap: "break-word",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[700],
              borderBottom: "none",
              fontSize: "14px",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[900],
              height: "5vh",
            },
            "& .MuiCheckbox-root": {
              color: `${colors.greenAccent[200]} !important`,
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${colors.grey[100]} !important`,
            },
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            checkboxSelection
            loading={loading}
            onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
            components={{ Toolbar: GridToolbar }}
            processRowUpdate={(updatedRow) => {
              setRows((prevRows) =>
                prevRows.map((row) => (row.id === updatedRow.id ? updatedRow : row))
              );
              return updatedRow;
            }}
            experimentalFeatures={{ newEditingApi: true }}
          />

          <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete {selectedRows.length} selected row(s)?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenConfirmDialog(false)} color="primary">Cancel</Button>
              <Button onClick={confirmDelete} color="error" variant="contained">Yes, Delete</Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </div>
  );
};

export default ViewBalance;